import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Chip,
    Card,
    CardContent,
    Alert,
    Snackbar,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Paper,
    Grid,
    IconButton,
    Tooltip,
} from '@mui/material';
import { IconMail, IconEye, IconSend, IconSave, IconRefresh, IconCode, IconEdit, IconX, IconCopy } from '@tabler/icons';
import { useQuery, useMutation, gql } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { Save } from '@mui/icons-material';

// GraphQL Queries
const GET_EMAIL_TEMPLATE = gql`
    query GetEmailTemplate($contextId: String!, $templateType: String!, $themeId: String) {
        getEmailTemplate(contextId: $contextId, templateType: $templateType, themeId: $themeId) {
            subject
            body
            isHtml
            createdAt
            updatedAt
        }
    }
`;

const GET_EMAIL_VARIABLES = gql`
    query GetEmailVariables($contextId: String!, $templateType: String!) {
        getEmailVariables(contextId: $contextId, templateType: $templateType) {
            name
            description
            example
        }
    }
`;

const GET_FORM_FIELDS = gql`
    query GetFormFields($eventId: String!, $role: ROLE!) {
        form(eventId: $eventId, role: $role) {
            fields {
                name
                label
                type
            }
        }
    }
`;

// GraphQL Mutations
const SAVE_EMAIL_TEMPLATE = gql`
    mutation SaveEmailTemplate(
        $contextId: String!
        $templateType: String!
        $subject: String!
        $body: String!
        $isHtmlMode: Boolean!
        $themeId: String
    ) {
        saveEmailTemplate(
            contextId: $contextId
            templateType: $templateType
            subject: $subject
            body: $body
            isHtmlMode: $isHtmlMode
            themeId: $themeId
        )
    }
`;

const CREATE_EMAIL_TEMPLATE = gql`
    mutation CreateEmailTemplate($eventId: String!, $input: CreateEmailTemplateInput!) {
        createEmailTemplate(eventId: $eventId, input: $input) {
            id
            role
            userId
            invitation_subject
            invitation_body
            invitation_is_html
            confirmation_subject
            confirmation_body
            confirmation_is_html
            accept_subject
            accept_body
            accept_is_html
            reject_subject
            reject_body
            reject_is_html
            created_at
            updated_at
        }
    }
`;

const UPDATE_EMAIL_TEMPLATE = gql`
    mutation UpdateEmailTemplate($eventId: String!, $input: UpdateEmailTemplateInput!) {
        updateEmailTemplate(eventId: $eventId, input: $input) {
            id
            role
            userId
            invitation_subject
            invitation_body
            invitation_is_html
            confirmation_subject
            confirmation_body
            confirmation_is_html
            accept_subject
            accept_body
            accept_is_html
            reject_subject
            reject_body
            reject_is_html
            created_at
            updated_at
        }
    }
`;

const SEND_TEST_EMAIL = gql`
    mutation SendTestEmail($contextId: String!, $templateType: String!, $testEmail: String!, $role: String!) {
        sendTestEmail(contextId: $contextId, templateType: $templateType, testEmail: $testEmail, role: $role)
    }
`;

// Email Template Manager Component
const EmailTemplateManager = ({
    contextId,
    templateType = 'invitation',
    title = 'Email Template Setup',
    eventId,
    role,
    themeId,
    onSave,
    onTest,
    initialSubject = '',
    initialBody = '',
    initialIsHtml = true,
    hideSaveButton = false,
    initialTemplate = null,
}) => {
    // Convert role to match ROLE enum format (most are lowercase, some have capitals like "Corporates")
    const convertRoleToEnum = roleString => {
        if (!roleString) return roleString;
        // Convert to lowercase first, then handle special cases
        let role = roleString.toLowerCase();

        // Handle special cases that have capital letters in the enum
        const roleMap = {
            corporates: 'Corporates',
            ctf: 'CTF',
            crisissimulation: 'CrisisSimulation',
            hackathon: 'Hackathon',
            daypass: 'DayPass',
            certificate: 'Certificate',
            student: 'Student',
            meeting: 'Meeting',
            vendors: 'Vendors',
            board_members: 'Board_Members',
            oshrm_board_members: 'OSHRM_Board_Members',
            nama_delegate: 'nama_delegate',
        };

        return roleMap[role] || role;
    };

    const enumRole = convertRoleToEnum(role);
    // State management
    const [subject, setSubject] = useState(initialSubject);
    const [htmlBody, setHtmlBody] = useState(initialBody);
    const [richTextBody, setRichTextBody] = useState(initialBody);
    const [isHtmlMode, setIsHtmlMode] = useState(initialIsHtml);
    const [testEmail, setTestEmail] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState({});
    const [testEmailDialog, setTestEmailDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [activeField, setActiveField] = useState('subject'); // Track which field is active
    const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' }); // For copy notifications

    // GraphQL operations
    // Determine if we should skip the old template query
    // Skip if: initial values provided OR no themeId (new template system)
    const shouldSkipOldQuery = !!initialTemplate || !!initialSubject || initialBody || !themeId;

    const {
        data: templateData,
        loading: templateLoading,
        refetch: refetchTemplate,
    } = useQuery(GET_EMAIL_TEMPLATE, {
        // Only provide variables if we're not skipping (to avoid unnecessary query construction)
        variables: shouldSkipOldQuery ? {} : { contextId, templateType, themeId },
        skip: shouldSkipOldQuery, // Skip if using new template system
        fetchPolicy: shouldSkipOldQuery ? 'no-cache' : 'cache-first', // Don't cache if skipping
        onCompleted: data => {
            // Only use data if we're not using the new template system
            if (data?.getEmailTemplate && !shouldSkipOldQuery) {
                setSubject(data.getEmailTemplate.subject || '');
                setHtmlBody(data.getEmailTemplate.body || '');
                setRichTextBody(data.getEmailTemplate.body || '');
                setIsHtmlMode(data.getEmailTemplate.isHtml || true);
            }
        },
        onError: error => {
            // Silently handle errors - we're using new template system which doesn't use this query
            // Only log if we actually expected this query to work (themeId is provided and no initial values)
            if (!shouldSkipOldQuery) {
                console.log('Template query error:', error.message);
            }
        },
        errorPolicy: 'ignore', // Ignore errors since we're using new template system
    });

    // Update state when initial values change
    React.useEffect(() => {
        if (initialSubject) setSubject(initialSubject);
        if (initialBody) {
            setHtmlBody(initialBody);
            setRichTextBody(initialBody);
        }
        if (initialIsHtml !== undefined) setIsHtmlMode(initialIsHtml);
    }, [initialSubject, initialBody, initialIsHtml]);

    const { data: variablesData, loading: variablesLoading } = useQuery(GET_EMAIL_VARIABLES, {
        variables: { contextId, templateType },
    });

    const { data: formData } = useQuery(GET_FORM_FIELDS, {
        variables: { eventId: contextId, role },
        skip: !contextId || !role,
    });

    const [saveTemplate, { loading: saving }] = useMutation(SAVE_EMAIL_TEMPLATE, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data.saveEmailTemplate || 'Template saved successfully',
                severity: 'success',
            });
            if (onSave) onSave();
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to save template',
                severity: 'error',
            });
        },
    });

    const [createTemplate, { loading: creating }] = useMutation(CREATE_EMAIL_TEMPLATE, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: 'Template created successfully',
                severity: 'success',
            });
            if (onSave) onSave();
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to create template',
                severity: 'error',
            });
        },
    });

    const [updateTemplate, { loading: updating }] = useMutation(UPDATE_EMAIL_TEMPLATE, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: 'Template updated successfully',
                severity: 'success',
            });
            if (onSave) onSave();
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to update template',
                severity: 'error',
            });
        },
    });

    const [sendTestEmail, { loading: testing }] = useMutation(SEND_TEST_EMAIL, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data.sendTestEmail || 'Test email sent successfully',
                severity: 'success',
            });
            setTestEmailDialog(false);
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to send test email',
                severity: 'error',
            });
        },
    });

    // Available variables - combine system variables with form fields
    const systemVariables = variablesData?.getEmailVariables || [];
    const formFields = formData?.form?.fields || [];

    // Create dynamic variables from form fields
    const dynamicVariables = formFields.map(field => ({
        name: field.name,
        description: `Form field: ${field.label || field.name}`,
        example: `Sample ${field.label || field.name}`,
    }));

    // System variables for invitation emails
    const invitationSystemVariables = [
        {
            name: 'confirmationLink',
            description: 'Link for attendee to confirm attendance',
            example: 'https://eventegration.com/confirm/abc123',
        },
        {
            name: 'ThemeName',
            description: 'Name of the theme',
            example: 'Theme Name',
        },
    ];

    const variables = [...systemVariables, ...dynamicVariables, ...invitationSystemVariables];

    // Sample data for preview - only system variables
    const sampleData = {
        confirmationLink: 'https://eventegration.com/confirm/abc123',
        ThemeName: 'AI Workshop',
        ThemeDescription: 'Learn about AI and Machine Learning',
        EventDate: '2024-01-15',
        EventTime: '10:00 AM - 12:00 PM',
        Venue: 'Conference Room A',
    };

    // Handle format toggle
    const handleFormatToggle = event => {
        const newMode = event.target.checked;
        setIsHtmlMode(newMode);

        if (newMode) {
            // Switching to HTML mode - convert rich text to HTML
            setHtmlBody(richTextBody);
        } else {
            // Switching to Rich Text mode - convert HTML to rich text
            setRichTextBody(htmlBody);
        }
    };

    // Insert variable into editor
    const insertVariable = variableName => {
        const variable = `$${variableName}`;
        if (activeField === 'subject') {
            setSubject(prev => prev + variable);
        } else if (activeField === 'htmlBody') {
            setHtmlBody(prev => prev + variable);
        } else if (activeField === 'richTextBody') {
            setRichTextBody(prev => prev + variable);
        }
    };

    const copyVariable = async variableName => {
        const variable = `$${variableName}`;
        try {
            await navigator.clipboard.writeText(variable);
            setCopySnackbar({
                open: true,
                message: `Copied ${variable} to clipboard`,
            });
        } catch (err) {
            console.error('Failed to copy: ', err);
            setCopySnackbar({
                open: true,
                message: 'Failed to copy to clipboard',
            });
        }
    };

    // Process template with variables
    const processTemplate = (templateSubject, templateBody, data) => {
        let processedSubject = templateSubject;
        let processedBody = templateBody;

        Object.keys(data).forEach(key => {
            const regex = new RegExp(`\\$${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
            processedSubject = processedSubject.replace(regex, data[key]);
            processedBody = processedBody.replace(regex, data[key]);
        });

        return { subject: processedSubject, body: processedBody };
    };

    // Handle save
    const handleSave = async () => {
        const body = isHtmlMode ? htmlBody : richTextBody;

        // If hideSaveButton is true, just call onSave with data (for inline editing)
        if (hideSaveButton && onSave) {
            onSave({
                subject,
                body,
                isHtml: isHtmlMode,
            });
            return;
        }

        // If initialTemplate is provided, update it; otherwise create new
        if (initialTemplate && initialTemplate.id && typeof initialTemplate.id === 'number') {
            await updateTemplate({
                variables: {
                    eventId: contextId,
                    input: {
                        id: initialTemplate.id,
                        templateType,
                        subject,
                        body,
                        isHtml: isHtmlMode,
                    },
                },
            });
        } else {
            // Create new template using createEmailTemplate mutation
            await createTemplate({
                variables: {
                    eventId: contextId,
                    input: {
                        role: enumRole,
                        templateType,
                        subject,
                        body,
                        isHtml: isHtmlMode,
                    },
                },
            });
        }
    };

    // Handle test email
    const handleSendTest = async () => {
        if (!testEmail.trim()) {
            setSnackbar({
                open: true,
                message: 'Please enter a test email address',
                severity: 'error',
            });
            return;
        }

        await sendTestEmail({
            variables: {
                contextId,
                templateType,
                testEmail: testEmail.trim(),
                role,
            },
        });
    };

    // Handle preview
    const handlePreview = () => {
        setPreviewData(sampleData);
        setShowPreview(!showPreview);
    };

    // Render preview
    const renderPreview = () => {
        const body = isHtmlMode ? htmlBody : richTextBody;
        const processedTemplate = processTemplate(subject, body, previewData);

        return (
            <Paper sx={{ mt: 2, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconEye size={20} />
                    Email Preview
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 600 }}>
                        Subject:
                    </Typography>
                    <Typography
                        variant='body1'
                        sx={{
                            fontStyle: 'italic',
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                        }}
                    >
                        {processedTemplate.subject}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 600 }}>
                        Body:
                    </Typography>
                    <Box
                        sx={{
                            mt: 1,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            bgcolor: 'white',
                            borderRadius: 1,
                            minHeight: 200,
                        }}
                        dangerouslySetInnerHTML={{ __html: processedTemplate.body }}
                    />
                </Box>
            </Paper>
        );
    };

    if (templateLoading || variablesLoading) {
        return <LinearProgress />;
    }

    return (
        <MainCard title={title}>
            {/* Format Toggle */}
            <Box sx={{ mb: 3 }}>
                <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconCode size={20} />
                    Email Format
                </Typography>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='body2' color={!isHtmlMode ? 'primary.main' : 'text.secondary'}>
                        Rich Text
                    </Typography>
                    <Switch checked={isHtmlMode} onChange={handleFormatToggle} color='primary' />
                    <Typography variant='body2' color={isHtmlMode ? 'primary.main' : 'text.secondary'}>
                        HTML
                    </Typography>
                </Stack>
            </Box>

            {/* Available Variables */}
            {variables.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconMail size={20} />
                        Available Variables
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Click on any variable to insert it into your template:
                    </Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        {variables.map((variable, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                    label={`$${variable.name}`}
                                    variant='outlined'
                                    size='small'
                                    onClick={() => insertVariable(variable.name)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                        },
                                    }}
                                />
                                <Tooltip title='Copy to clipboard'>
                                    <IconButton
                                        size='small'
                                        onClick={e => {
                                            e.stopPropagation();
                                            copyVariable(variable.name);
                                        }}
                                        sx={{
                                            p: 0.5,
                                            '&:hover': {
                                                bgcolor: 'primary.light',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <IconCopy size={14} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Subject Input */}
            <Box sx={{ mb: 3 }}>
                <Typography variant='h6' gutterBottom>
                    Email Subject
                </Typography>
                <TextField
                    fullWidth
                    placeholder='Enter email subject (use variables like $confirmationLink)'
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    onFocus={() => setActiveField('subject')}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'primary.main',
                            },
                        },
                    }}
                />
            </Box>

            {/* Body Editor */}
            <Box sx={{ mb: 3 }}>
                <Typography variant='h6' gutterBottom>
                    Email Body
                </Typography>
                {isHtmlMode ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        placeholder='<html><body><h1>Hello there!</h1><p>Welcome to $ThemeName!</p><p>Click here to confirm: $confirmationLink</p></body></html>'
                        value={htmlBody}
                        onChange={e => setHtmlBody(e.target.value)}
                        onFocus={() => setActiveField('htmlBody')}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                    />
                ) : (
                    <Box
                        sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}
                        onClick={() => setActiveField('richTextBody')}
                    >
                        <ReactQuill
                            theme='snow'
                            value={richTextBody}
                            onChange={setRichTextBody}
                            modules={{
                                toolbar: [
                                    [{ header: [1, 2, 3, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ color: [] }, { background: [] }],
                                    [{ list: 'ordered' }, { list: 'bullet' }],
                                    ['link', 'image'],
                                    ['clean'],
                                ],
                            }}
                            style={{
                                minHeight: '300px',
                                '& .ql-editor': {
                                    minHeight: '250px',
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* Action Buttons */}
            {!hideSaveButton ? (
                <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
                    <AnimateButton>
                        <Button
                            variant='contained'
                            startIcon={<Save size={20} />}
                            onClick={handleSave}
                            disabled={saving || creating || updating}
                            sx={{
                                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                                },
                            }}
                        >
                            {saving ? 'Saving...' : 'Save Template'}
                        </Button>
                    </AnimateButton>

                    <Button
                        variant='outlined'
                        startIcon={<IconEye size={20} />}
                        onClick={handlePreview}
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                bgcolor: 'primary.light',
                            },
                        }}
                    >
                        {showPreview ? 'Hide Preview' : 'Preview'}
                    </Button>

                    <Button
                        variant='outlined'
                        startIcon={<IconSend size={20} />}
                        onClick={() => setTestEmailDialog(true)}
                        sx={{
                            borderColor: 'secondary.main',
                            color: 'secondary.main',
                            '&:hover': {
                                borderColor: 'secondary.dark',
                                bgcolor: 'secondary.light',
                            },
                        }}
                    >
                        Send Test Email
                    </Button>
                </Stack>
            ) : (
                <Stack direction='row' spacing={2} sx={{ mb: 3, justifyContent: 'flex-end' }}>
                    <Button
                        variant='outlined'
                        onClick={() => {
                            if (onSave) {
                                onSave({ subject: initialSubject, body: initialBody, isHtml: initialIsHtml });
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        startIcon={<Save size={20} />}
                        onClick={handleSave}
                        disabled={saving || creating || updating}
                    >
                        {saving ? 'Saving...' : 'Apply Changes'}
                    </Button>
                </Stack>
            )}

            {/* Preview Section */}
            {showPreview && renderPreview()}

            {/* Test Email Dialog */}
            <Dialog open={testEmailDialog} onClose={() => setTestEmailDialog(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Send Test Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label='Test Email Address'
                        type='email'
                        value={testEmail}
                        onChange={e => setTestEmail(e.target.value)}
                        placeholder='Enter email address to send test to'
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestEmailDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSendTest}
                        variant='contained'
                        disabled={testing || !testEmail.trim()}
                        startIcon={<IconSend size={20} />}
                    >
                        {testing ? 'Sending...' : 'Send Test'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Copy notification snackbar */}
            <Snackbar
                open={copySnackbar.open}
                autoHideDuration={3000}
                onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
            >
                <Alert
                    onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
                    severity='success'
                    sx={{ width: '100%' }}
                >
                    {copySnackbar.message}
                </Alert>
            </Snackbar>
        </MainCard>
    );
};

export default EmailTemplateManager;
